import { DEFAULT_LOCALE, resolveSupportedLocale, type Locale } from "@/i18n/locales"
import { isEmpty } from "@/lib/helpers/is-empty"
import { currencyFormatter, rendersBareDollar } from "@/lib/money/currency-format"
import type { RegionInfo } from "@/lib/types/region"

/**
 * Parses a formatted currency string to a number
 * @param displayValue - Formatted currency string (e.g., "$1,234.56")
 * @returns Parsed number value, or 0 if invalid
 */
export const parseCurrencyString = (displayValue: string): number => {
  if (!displayValue || displayValue.trim() === "") return 0
  const sanitized = displayValue.replace(/[^0-9.-]+/g, "")
  const parsed = parseFloat(sanitized)
  return isNaN(parsed) ? 0 : parsed
}

interface ComputeAmountParams {
  amount: number
  region: RegionInfo
  includeTaxes?: boolean
}
/**
 * Takes an amount, a region, and returns the amount as a decimal including or excluding taxes
 */
const computeAmount = ({ amount, region, includeTaxes = true }: ComputeAmountParams) => {
  const toDecimal = convertToDecimal(amount, region)
  const taxRate = includeTaxes ? getTaxRate(region) : 0
  return toDecimal * (1 + taxRate)
}

interface FormatAmountParams {
  amount: number
  region: RegionInfo
  includeTaxes?: boolean
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  locale?: string
}
/**
 * Takes an amount and a region, and converts the amount to a localized decimal format
 */
export const formatAmount = ({ amount, region, includeTaxes = true, ...rest }: FormatAmountParams) => {
  const taxAwareAmount = computeAmount({
    amount,
    region,
    includeTaxes,
  })

  return convertToLocale({
    amount: taxAwareAmount,
    currency_code: region.currency_code,
    ...rest,
  })
}
// Add currencies that don't need to be divided by 100
const noDivisionCurrencies = ["krw", "jpy", "vnd", "clp", "pyg", "xaf", "xof", "bif", "djf", "gnf", "kmf", "mga", "rwf", "xpf", "htg", "vuv", "xag", "xdr", "xau"]

const convertToDecimal = (amount: number, region: RegionInfo) => {
  const divisor = noDivisionCurrencies.includes(region?.currency_code?.toLowerCase()) ? 1 : 100

  return Math.floor(amount) / divisor
}

const getTaxRate = (region?: RegionInfo) => {
  return region && !isEmpty(region) ? region?.tax_rate / 100 : 0
}

interface ConvertToLocaleParams {
  amount: number
  currency_code: string
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  locale?: string
}

// Nelo is a Mexican-peso marketplace, but a locale that renders MXN as a bare "$"
// leaves ad viewers reading the amount as USD/EUR. Tag those with a "MX" suffix; a
// locale that already renders "MX$" needs no tag.
const PESO_SUFFIX = " MX"

const PESO_CURRENCY = "MXN"

const pesoTag = (locale: Locale) => (rendersBareDollar(locale, PESO_CURRENCY) ? PESO_SUFFIX : "")

const formatPesos = (amount: number, minimumFractionDigits = 2, maximumFractionDigits = 2, locale: Locale = DEFAULT_LOCALE): string =>
  `${currencyFormatter({ locale, currency: PESO_CURRENCY, minimumFractionDigits, maximumFractionDigits }).format(amount)}${pesoTag(locale)}`

export const convertToLocale = ({ amount, currency_code, minimumFractionDigits, maximumFractionDigits, locale }: ConvertToLocaleParams) => {
  if (!currency_code || isEmpty(currency_code)) return amount.toString()

  const resolved = resolveSupportedLocale(locale)

  if (currency_code.toLowerCase() === PESO_CURRENCY.toLowerCase()) {
    return formatPesos(amount, minimumFractionDigits ?? 2, maximumFractionDigits ?? 2, resolved)
  }

  return currencyFormatter({ locale: resolved, currency: currency_code, minimumFractionDigits, maximumFractionDigits }).format(amount)
}

/**
 * The store API returns prices in cents (e.g., 150000 = $1,500.00).
 */
export const centsToDollars = (cents: number): number => cents / 100

/**
 * Formats a peso amount as a price string with an explicit MX suffix
 * @param dollars - The peso amount to format
 * @returns Formatted string like "$1,234.56 MX"
 */
export const formatPrice = (dollars: number | null | undefined, locale: Locale = DEFAULT_LOCALE): string => formatPesos(dollars ?? 0, 2, 2, locale)

/**
 * Formats a peso amount as a price string WITHOUT the MX suffix, for browse
 * surfaces (home, search, category, related-product cards) where the tag is
 * clutter. The MX-tagged formatPrice is reserved for PDP and the checkout flow.
 * @returns Formatted string like "$1,234.56"
 */
export const formatPricePlain = (dollars: number | null | undefined, locale: Locale = DEFAULT_LOCALE): string =>
  currencyFormatter({ locale, currency: PESO_CURRENCY, minimumFractionDigits: 2, maximumFractionDigits: 2, currencyDisplay: "narrowSymbol" }).format(dollars ?? 0)

/**
 * Builds the "4 pagos de …" installment amount for product cards. Returns
 * undefined for missing, zero, or negative prices so cards never advertise
 * "4 pagos de $0.00" when the API sends a broken price.
 */
export const installmentLabel = (dollars: number | null | undefined, format: (dollars: number) => string = formatPricePlain): string | undefined =>
  dollars != null && dollars > 0 ? format(dollars / 4) : undefined

/**
 * Tags each "$" amount in a server-formatted string (which arrives with a bare
 * "$") with a trailing " MX", leaving already-tagged amounts untouched. Drop
 * this once nelo-api returns the suffix itself.
 */
export const ensurePesoSuffix = (formatted: string, locale: Locale = DEFAULT_LOCALE): string => {
  const tag = pesoTag(locale)
  if (!tag) return formatted
  return formatted.replace(/\$\d+(?:,\d{3})*(?:\.\d{2})?(?!\d|,\d{3}|\.\d| MX)/g, (match) => `${match}${tag}`)
}

/**
 * Convert a dollar amount to `amountE5` — the 5-decimal fixed-point integer
 * used by Nelo's WireMoney shape ($1 → 100_000).
 */
export const dollarsToAmountE5 = (dollars: number): number => Math.round(dollars * 100_000)

/**
 * Convert a store-API cents amount to `amountE5`, which adds three more
 * decimals of precision (cents × 1000 = dollars × 100_000).
 */
export const centsToAmountE5 = (cents: number): number => Math.round(cents * 1000)
